import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { tap, concatMap, Subscription, map, mergeMap } from 'rxjs';
import { CookieService } from 'ngx-cookie';

import { AuthService } from '@shared/services/auth/auth.service';
import { SessionService } from '@shared/services/auth/session.service';
import { PageTitleService } from '@shared/services/core/page-title.service';
import { EncryptionService } from '@shared/services/core/encryption.service';
import { LogService } from '@shared/services/core/log.service';

import {
  ClientUserData,
  ProfessionalUserData,
  UserLicenseDetail,
  CookieData,
  UnspecifiedUserTypeData
} from '@shared/models/auth.interface';
import { DashboardService } from '@shared/services/dashboard/dashboard.service';
import { BusinessError, CategorizedError } from '@modules/error/classes';
import { SnackbarService } from '@shared/services/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Menu } from '@shared/models/sidenav.interface';


@Component({
  selector: 'app-app-initialization',
  templateUrl: './app-initialization.page.html',
  styleUrls: ['./app-initialization.page.scss']
})
export class AppInitializationPage implements OnInit, OnDestroy {
  private _subscriptions: Array<Subscription> = [];
  private _jtiProvided: string = '';
  private _tokenProvided: string = '';
  private _refreshTokenProvided: string = '';
  private _newToken: string = '';
  private _newRefreshToken: string = '';
  private _isLoggedIn: boolean = false;
  private _pageLoadTimeout: number = 3000;
  activeUserLogo!: string;
  defaultLogo: string = '/assets/images/entireonhire-logo.svg';


  constructor(
    private _router: Router,
    private _activeRoute: ActivatedRoute,
    private _authService: AuthService,
    private _cookieService: CookieService,
    private _sessionService: SessionService,
    private _dashboardService: DashboardService,
    private _encryptionService: EncryptionService,
    private _logService: LogService,
    private _pageTitleService: PageTitleService,
    private _snackbarService: SnackbarService,
    private deviceDetectorService: DeviceDetectorService
  ) { }

  ngOnInit(): void {
    this._pageTitleService.setTitleBasedOnRoute();
    this._jtiProvided = this._activeRoute.snapshot.queryParams["args1"];
    this._tokenProvided = this._activeRoute.snapshot.queryParams["args2"];
    this._refreshTokenProvided = this._activeRoute.snapshot.queryParams["args3"];
    this._isLoggedIn = (this._activeRoute.snapshot.queryParams["args4"] === "y");

    let logo = this._sessionService.getLogo();
    this.activeUserLogo = (logo) ?? this.defaultLogo;

    if(this._isLoggedIn) this.checkKeepMeLoggedIn();

    if(this._jtiProvided && this._tokenProvided && this._refreshTokenProvided && this._isLoggedIn) {
      this.authenticateLoggedInUser(this._jtiProvided, this._tokenProvided, this._refreshTokenProvided);
    }
    else if (this._jtiProvided && this._tokenProvided) {
      this.authenticateCredentials(this._jtiProvided, this._tokenProvided);
    } else {
      this._authService.removeKeepMeLoggedIN();
      this._router.navigateByUrl("/login");
    }
  }

  authenticateLoggedInUser(jti: string, currentToken: string, currentRefreshToken: string) {
    let userID: string = '';
    let userLicenseDetails: UserLicenseDetail;
    let userType:"CLT"|"PRF" = "CLT";
    let forClient = true;
    let userDetails: UnspecifiedUserTypeData;

    const authenticationSubscription = this._authService.getRefreshedToken(currentToken, currentRefreshToken).pipe(
      tap(refreshTokenResponse => {
        this._newToken = refreshTokenResponse.data.token;
        this._newRefreshToken = refreshTokenResponse.data.refreshToken;
        this._sessionService.updateTokenAndJTIOnStorage(jti, this._newToken);
      }),
      concatMap(_ => this._authService.getUserIDAndRefreshToken({ jti, token: this._newToken })),
      tap(userIDAndRefreshTokenResponse => { 
        userID = userIDAndRefreshTokenResponse.Data.UserId; // and we already have refreshed token.
      }),
      concatMap(_ => this._authService.getLicenseDetails(this._newToken, this._jtiProvided)),
      tap(licenseDetailsResponse => { 
        this._authService.checkLicenseExpiry(licenseDetailsResponse.Data);
        userLicenseDetails = licenseDetailsResponse.Data;
      }),
      concatMap(_ => this._authService.getUserTypeDetailsByID(userID, jti, this._newToken)),
      tap(userDetailsResponse => userDetails = userDetailsResponse.Data),
      concatMap(_ => this._authService.performProfessionalActivationCheck(userID, this._newToken, jti)),
      concatMap(userAllowedToLogin => {
        if (!userAllowedToLogin) throw new BusinessError('USER_NOT_LINKED_WITH_CLIENTS', 'Professional not linked with any active clients.');

        if (userDetails.hasOwnProperty('ClientID')) {
          forClient = true;
          userType = 'CLT';
          this.saveUserData(true, userDetails, userLicenseDetails, this._newToken, this._newRefreshToken, jti);
          return this._authService.updateLoginLogoutStatus(this._newToken, this._jtiProvided, userID, userType, "Y").pipe(
            mergeMap(_ => this._dashboardService.getMenuListData(userType, "1", this._newToken, this._jtiProvided))
          );
        }
        else {
          forClient = false;
          userType = 'PRF';
          this.saveUserData(false, userDetails, userLicenseDetails, this._newToken, this._newRefreshToken, jti);
          return this._authService.updateLoginLogoutStatus(this._newToken, this._jtiProvided, userID, userType, "Y").pipe(
            mergeMap(_ => this._dashboardService.getMenuListData(userType, "0", this._newToken, (userDetails as ProfessionalUserData).LevelName))
          );
        }
      }),
      // map(menuListDataResponse => menuListDataResponse.Data),  // returning the 1st element as the list comes already sorted.
      map((menuOptions: Array<Menu>) => this.getLandingPageRoute(forClient, this._jtiProvided, menuOptions[0].optionPath))
    )
    .subscribe({
      next: (landingPage) => {
        if(this._isLoggedIn) {
          this._authService.removeKeepMeLoggedIN();
          const sessionCookie = this._authService.saveAllDataInCookie(jti, this._newToken, this._newRefreshToken);
          this._authService.setKeepMeLoggedIn(this._encryptionService.encryptData(sessionCookie));
        }
        else this._authService.removeKeepMeLoggedIN();

        setTimeout(() => {
          const usingMobileDevice = this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
          this._logService.appLogin("LoggedIn", { jti: this._jtiProvided, user_id: userID }, usingMobileDevice, this._newToken, this._jtiProvided);
          this._router.navigateByUrl(`/${(landingPage as string[]).join('/')}`);
        }, this._pageLoadTimeout);
      },

      error: (errorResponse) => {
        this._authService.removeKeepMeLoggedIN();

        let loginPageArgs = '';
        if (errorResponse instanceof BusinessError) loginPageArgs = `?authStatus=${errorResponse.title}`;
        else if (errorResponse instanceof CategorizedError) {
          this._snackbarService.showErrorSnackBar(errorResponse.title, errorResponse.description, 'none');
        } else loginPageArgs = '?authStatus=UNKNOWN_ERROR';
        setTimeout(()=> this._router.navigateByUrl('/login' + loginPageArgs), this._pageLoadTimeout);
      }
    });

    this._subscriptions.push(authenticationSubscription);
  }

  authenticateCredentials(jti: string, tokenProvided: string) {
    let refreshToken: string = '';
    let userID: string = '';
    let userType:"CLT"|"PRF" = "CLT";
    let forClient = true;
    let userLicenseDetails: UserLicenseDetail;
    let userDetails: UnspecifiedUserTypeData;

    const credentialSubscription = this._authService.getUserIDAndRefreshTokenJti({ jti, token: tokenProvided }).pipe(
      tap(userIDAndRefreshTokenResponse => {
        refreshToken = userIDAndRefreshTokenResponse.Data.RefreshToken;
        userID = userIDAndRefreshTokenResponse.Data.UserId;
      }),
      concatMap( _ =>  this._authService.getRefreshedToken(tokenProvided, refreshToken)),
      tap(refreshTokenResponse => {
        this._newToken = refreshTokenResponse.data.token;
        this._newRefreshToken = refreshTokenResponse.data.refreshToken;
        this._sessionService.updateTokenAndJTIOnStorage(jti, this._newToken);
      }),
      concatMap(_ => this._authService.getLicenseDetails(this._newToken, this._jtiProvided)),
      tap(licenseDetailsResponse => {
        this._authService.checkLicenseExpiry(licenseDetailsResponse.Data);
        userLicenseDetails = licenseDetailsResponse.Data;
      }),
      concatMap(_ => this._authService.getUserTypeDetailsByID(userID, jti, this._newToken)),
      tap(userDetailsResponse => userDetails = userDetailsResponse.Data),
      concatMap(_ => this._authService.performProfessionalActivationCheck(userID, this._newToken, jti)),
      concatMap(userAllowedToLogin => {
        if (!userAllowedToLogin) throw new BusinessError('USER_NOT_LINKED_WITH_CLIENTS', 'Professional not linked with any active clients.');

        if (userDetails.hasOwnProperty('ClientID')) {
          forClient = true;
          userType = 'CLT';
          this.saveUserData(true, userDetails, userLicenseDetails, this._newToken, refreshToken, jti);
          return this._authService.updateLoginLogoutStatus(this._newToken, this._jtiProvided, userID, userType, "Y").pipe(
            mergeMap(_ =>this._dashboardService.getMenuListData(userType, "1", this._newToken, this._jtiProvided))
          );
        }
        else {
          forClient = false;
          userType = 'PRF';
          this.saveUserData(false, userDetails, userLicenseDetails, this._newToken, refreshToken, jti);
          return this._authService.updateLoginLogoutStatus(this._newToken, this._jtiProvided, userID, userType, "Y").pipe(
            mergeMap(_ =>this._dashboardService.getMenuListData(userType, "0", this._newToken, this._jtiProvided, (userDetails as ProfessionalUserData).LevelName))
          );
        }
      }),
      // map(menuListDataResponse => menuListDataResponse.Data),  // returning the 1st element as the list comes already sorted.
      map((menuOptions: Array<Menu>) => this.getLandingPageRoute(forClient, this._jtiProvided, menuOptions[0].optionPath))
    )
    .subscribe({
      next: (landingPage) => {
        this._authService.removeKeepMeLoggedIN();

        setTimeout(() => {
          const usingMobileDevice = this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
          this._logService.appLogin("LoggedIn", { jti: this._jtiProvided, user_id: userID }, usingMobileDevice, this._newToken, this._jtiProvided);
          this._router.navigateByUrl(`/${(landingPage as string[]).join('/')}`);
        }, this._pageLoadTimeout);
      },

      error: (errorResponse) => {
        this._authService.removeKeepMeLoggedIN();

        let loginPageArgs = '';
        if (errorResponse instanceof BusinessError) loginPageArgs = `?authStatus=${errorResponse.title}`;
        else if (errorResponse instanceof CategorizedError) {
          this._snackbarService.showErrorSnackBar(errorResponse.title, errorResponse.description, 'none');
        } else loginPageArgs = '?authStatus=UNKNOWN_ERROR';
        setTimeout(()=> this._router.navigateByUrl('/login' + loginPageArgs), this._pageLoadTimeout);
      }
    });

    this._subscriptions.push(credentialSubscription);
  }

  checkKeepMeLoggedIn() {
    if(this._cookieService.hasKey(this._authService.encodeBase64("EntireClientPortal")) === true) {
      let cookieData = {} as CookieData;

      let obj: string = this._cookieService.get(this._authService.encodeBase64("EntireClientPortal")) || '';
      let encData: string = this._encryptionService.decryptData(obj);

      cookieData = JSON.parse(encData);

      this._jtiProvided = cookieData.jti;
      this._tokenProvided = cookieData.token;
      this._refreshTokenProvided = cookieData.refreshToken;
    }
  }

  saveUserData(forClient: boolean, userDetails: UnspecifiedUserTypeData, userLicense: UserLicenseDetail, token: string, refreshToken: string, jti: string) {
    // userIsClient = this.authService.determineIfUserIsClient(userDetails);
    if (forClient) {
      userDetails = userDetails as Exclude<UnspecifiedUserTypeData, ProfessionalUserData>;
      this._authService.saveAllUserDataOnSystemStorage<ClientUserData>(forClient, userDetails, userLicense, token, refreshToken, jti);
    } else {
      userDetails = userDetails as Exclude<UnspecifiedUserTypeData, ClientUserData>;
      this._authService.saveAllUserDataOnSystemStorage<ProfessionalUserData>(forClient, userDetails, userLicense, token, refreshToken, jti);
    }
  }

  getLandingPageRoute(forClient: boolean, jti: string, page: string): Array<string> {
    let landingPageRoute = ['userType', jti, page ];
    landingPageRoute[0] = (forClient) ? 'client': 'professional';
    return landingPageRoute;
  }


  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
